import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, Eye, Tag, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/posts/${slug}`)
      .then(r => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-gray-300" />
    </div>
  );

  if (notFound || !post) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-black text-gray-900 mb-4">Post Not Found</h1>
        <Link to="/blog" className="text-fire hover:underline font-medium">Back to Blog</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      {/* Cover */}
      {post.cover_image && (
        <div className="w-full h-64 md:h-96 overflow-hidden">
          <img src={post.cover_image} alt={post.title}
            className="w-full h-full object-cover" />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-4 py-10 md:py-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-fire transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-fire/10 text-fire rounded-full">
                <Tag className="w-3 h-3" />{t}
              </span>
            ))}
          </div>
        )}

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 leading-tight mb-4">
          {post.title}
        </h1>

        {post.excerpt && (
          <p className="text-lg text-gray-500 leading-relaxed mb-6 border-l-4 border-fire pl-4">
            {post.excerpt}
          </p>
        )}

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-10 pb-6 border-b border-gray-100">
          {post.author && <span className="font-medium text-gray-600">By {post.author}</span>}
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{formatDate(post.published_at)}</span>
          <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" />{(post.views || 0).toLocaleString()} views</span>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-gray max-w-none
          prose-headings:font-black prose-headings:text-gray-900 prose-headings:tracking-tight
          prose-p:text-gray-600 prose-p:leading-relaxed
          prose-a:text-fire prose-a:no-underline hover:prose-a:underline
          prose-img:rounded-xl
          prose-strong:text-gray-900
          prose-blockquote:border-fire prose-blockquote:text-gray-500"
          data-testid="blog-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* CTA */}
        <div className="mt-12 p-8 bg-gray-950 rounded-2xl text-center">
          <h3 className="text-xl font-bold text-white mb-2">Ready to transform your walls?</h3>
          <p className="text-white/60 text-sm mb-5">Browse our collection of 40,000+ premium posters and wall art.</p>
          <Link to="/shop" className="inline-block px-8 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-semibold rounded-lg hover:from-red-600 hover:to-orange-600 transition-all">
            Shop All Posters
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
